import { useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useActiveCapabilities } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import type { CommandContext } from './types';

/**
 * Build the side-effect surface that command `execute()` functions run
 * against. Returns a memoized `CommandContext` that's stable across
 * renders so passing it into a `useCallback`'s dep array doesn't
 * re-create the executor on every keystroke.
 *
 * `getState` reads through the redux store at call time rather than
 * snapshotting the state up front — same approach `GlobalMapSearch.tsx`
 * already uses for `useStore()` reads inside `triggerSearch` to dodge
 * render-batching gaps between dispatch and the next render.
 */
export default function useCommandContext(): CommandContext {
  const dispatch = useDispatch();
  const store = useStore();
  const capabilities = useActiveCapabilities();

  return useMemo<CommandContext>(
    () => ({
      dispatch,
      getState: () => store.getState(),
      capabilities: capabilities ?? {},
    }),
    [dispatch, store, capabilities],
  );
}
